<script setup lang="ts">
import { useStore } from "vuex";
import { themeColorStore } from "@/store/themeColor";
import { computed, PropType } from "vue";
import { useRouter } from "vue-router";

import {
  IconCircleDoneThin,
  IconWarningThin,
  IconCircleInfoThin,
  IconCloseThin,
} from "@packages/icon-library";
import { Notify } from "../types";
import { isButtonNotify, isLinkNotify, isNamedLinkNotify } from "../guards";
import { useMainNotifyStore } from "../store";

defineOptions({ name: "MainNotify" });

const props = defineProps({
  notify: {
    type: Object as PropType<Notify>,
    required: true,
  },
});

const themeStore = themeColorStore();
const router = useRouter();
const vuexStore = useStore();
const notifyStore = useMainNotifyStore();

const statusIcon = {
  error: IconWarningThin,
  success: IconCircleDoneThin,
  info: IconCircleInfoThin,
};

const shadowHigh = computed(() => themeStore.shadowHigh);

const isExternalLink = computed(() => {
  return (
    isLinkNotify(props.notify) &&
    !isNamedLinkNotify(props.notify) &&
    props.notify.link.to.startsWith("http")
  );
});

const linkComponent = computed(() => {
  if (isButtonNotify(props.notify)) {
    return "button";
  }
  return isExternalLink.value ? "a" : "RouterLink";
});

const linkProps = computed(() => {
  if (isButtonNotify(props.notify)) {
    return {
      type: "button",
      onclick: props.notify.button.action,
    };
  }

  if (isExternalLink.value) {
    return {
      href: props.notify.link.to,
      target: "_blank",
      rel: "noopener noreferrer",
    };
  }

  return {
    to: { name: nameLink.value },
  };
});

const nameLink = computed(() => {
  if (isNamedLinkNotify(props.notify)) {
    return props.notify.link.to.name;
  }

  if (isLinkNotify(props.notify)) {
    for (const route of router.getRoutes()) {
      if (route.path === `/${props.notify.link.to}`) {
        return route.name;
      }
    }
  }

  return "";
});

const actionBtnLabel = computed(() =>
  isButtonNotify(props.notify)
    ? props.notify.button.label
    : props.notify.link.label
);

const close = async () => {
  if (props.notify.type !== "debt" && props.notify.type !== "profile") {
    await vuexStore.dispatch("auth/notificationMarkAsReady", props.notify.id);
  }

  notifyStore.changeNotifyVisibility(false);
};
</script>

<template>
  <Transition name="notify">
    <section
      v-if="notifyStore.isOpen"
      class="main-notify"
      :class="{
        'main-notify_warning': notify.status === 'error',
        'main-notify_success': notify.status === 'success',
        'main-notify_info': notify.status === 'info',
        [shadowHigh]: true,
      }"
    >
      <component :is="statusIcon[props.notify.status]" />

      <div class="main-notify__info">
        <h5 v-html="notify.title" />

        <p v-html="notify.text" />

        <component :is="linkComponent" v-bind="linkProps">
          {{ actionBtnLabel }}
        </component>
      </div>

      <button class="main-notify__close" type="button" @click="close">
        <IconCloseThin />
      </button>
    </section>
  </Transition>
</template>

<style lang="scss" scoped>
.main-notify {
  position: absolute;
  top: 73px;
  right: 48px;
  max-width: 400px;
  height: fit-content;
  background: var(--color-semantic-background-normal-primary);
  border-radius: 4px;
  z-index: 1000003;
  display: flex;
  gap: 12px;
  padding: 16px 16px 16px 20px;
  overflow: hidden;
  color: var(--color-semantic-content-normal-primary);

  &::before {
    position: absolute;
    top: 0;
    left: 0;
    content: "";
    width: 4px;
    height: 100%;
  }

  > :first-child {
    flex: 0 0 auto;
    width: 32px;
    height: 32px;
  }

  &_warning {
    &::before,
    > ::v-deep(.icon) {
      background-color: var(--color-semantic-content-normal-negative);
    }
  }

  &_success {
    &::before,
    > ::v-deep(.icon) {
      background-color: var(--color-semantic-content-normal-positive);
    }
  }

  &_info {
    &::before,
    > ::v-deep(.icon) {
      background-color: var(--color-semantic-content-normal-info);
    }
  }

  &__info {
    > h5 {
      @include body-M;
      margin-bottom: 4px;
    }

    > p {
      @include body-S;
      color: var(--color-semantic-content-normal-tertiary);
      margin-bottom: 12px;
    }

    > a,
    > button {
      text-decoration: none;
      color: var(--color-semantic-content-normal-link);
      @include body-S;
    }
  }

  &__close {
    line-height: 0;
    height: min-content;

    .icon {
      width: 24px;
      height: 24px;
      background-color: var(--color-semantic-content-normal-tertiary);
    }
  }

  @media (max-width: 767px) {
    padding: 12px 12px 12px 16px;
    gap: 8px;

    > :first-child {
      width: 24px;
      height: 24px;
    }

    &__close .icon {
      width: 20px;
      height: 20px;
    }

    &__info p {
      margin-bottom: 8px;
    }
  }
}

.notify-enter-active {
  animation: notify-show 0.5s ease-out;
}

.notify-leave-active {
  animation: notify-show 0.5s reverse ease-out;
}

@keyframes notify-show {
  0% {
    opacity: 0;
    right: -100%;
  }

  100% {
    opacity: 1;
    right: 48px;
  }
}

@media (max-width: 1535px) {
  .main-notify {
    right: 32px;
  }
}

@media (max-width: 767px) {
  .main-notify {
    width: 90%;
    right: 50%;
    transform: translate(50%, 5px);
  }

  @keyframes notify-show {
    0% {
      opacity: 0;
      top: -100%;
    }

    100% {
      opacity: 1;
      top: 73px;
    }
  }
}

@media (max-width: 411px) {
  .main-notify {
    width: 80%;
  }
}
</style>
